'use client';

import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useEffect, useRef } from 'react';
import { IntentPrefetchLink } from '@/components/ui/intent-prefetch-link';
import { type SocialLink, socialIcons } from '@/components/ui/social-icons';
export interface SocialProofItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  posterUrl?: string;
  alt: string;
  href?: string;
}
interface SocialProofClientProps {
  heading?: string;
  handle?: string;
  socialLinks?: SocialLink[];
  items: SocialProofItem[];
  aspectRatio?: string;
  autoplayVideos?: boolean;
}
const ASPECT_CLASSES: Record<string, string> = {
  '3/4': 'aspect-[3/4]',
  '2/3': 'aspect-[2/3]',
  '1/1': 'aspect-square',
  '4/3': 'aspect-[4/3]'
};
const CARD_SIZES = '(min-width: 1280px) 18vw, (min-width: 768px) 33vw, 70vw';
export function SocialProofClient(t0) {
  const $ = _c(23);
  const {
    heading,
    handle,
    socialLinks: t1,
    items,
    aspectRatio: t2,
    autoplayVideos: t3
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const socialLinks = t4;
  const aspectRatio = t2 === undefined ? "3/4" : t2;
  const autoplayVideos = t3 === undefined ? true : t3;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps",
      skipSnaps: false
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const [emblaRef] = useEmblaCarousel(t5);
  if (items.length === 0) {
    return null;
  }
  const aspectClass = ASPECT_CLASSES[aspectRatio] || ASPECT_CLASSES["3/4"];
  const hasHeader = Boolean(heading || handle || socialLinks.length > 0);
  let t6;
  if ($[3] !== handle || $[4] !== hasHeader || $[5] !== heading || $[6] !== socialLinks) {
    t6 = hasHeader && <div className="mb-6 flex flex-wrap items-center justify-between gap-4">{heading && <h2 className="text-2xl font-black uppercase tracking-tight md:text-3xl lg:text-4xl">{heading}</h2>}{(handle || socialLinks.length > 0) && <div className="flex items-center gap-3">{handle && <span className="text-sm font-bold uppercase tracking-wide text-neutral-900">{handle}</span>}{socialLinks.map(_temp)}</div>}</div>;
    $[3] = handle;
    $[4] = hasHeader;
    $[5] = heading;
    $[6] = socialLinks;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== aspectClass || $[9] !== autoplayVideos || $[10] !== items) {
    let t8;
    if ($[12] !== aspectClass || $[13] !== autoplayVideos) {
      t8 = item => <div key={item.id} className="min-w-0 shrink-0 basis-[70%] pl-3 sm:basis-[44%] md:basis-1/3 md:pl-4 lg:basis-1/4 xl:basis-1/5"><SocialProofCard item={item} aspectClass={aspectClass} autoplay={autoplayVideos} /></div>;
      $[12] = aspectClass;
      $[13] = autoplayVideos;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t7 = items.map(t8);
    $[8] = aspectClass;
    $[9] = autoplayVideos;
    $[10] = items;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <div className="-ml-3 flex md:-ml-4">{t7}</div>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== emblaRef || $[18] !== t8) {
    t9 = <div ref={emblaRef} className="cursor-grab overflow-hidden active:cursor-grabbing">{t8}</div>;
    $[17] = emblaRef;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t9) {
    t10 = <div>{t6}{t9}</div>;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp(social) {
  const Icon = socialIcons[social.platform];
  if (!Icon) {
    return null;
  }
  return <a key={`${social.platform}-${social.url}`} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={social.platform} className="text-neutral-900 transition-colors hover:text-neutral-500"><Icon className="h-5 w-5" /></a>;
}
function SocialProofCard(t0) {
  const $ = _c(10);
  const {
    item,
    aspectClass,
    autoplay
  } = t0;
  let t1;
  if ($[0] !== autoplay || $[1] !== item) {
    t1 = item.type === "video" ? <VideoMedia item={item} autoplay={autoplay} /> : <Image src={item.url} alt={item.alt} fill={true} className="object-cover" sizes={CARD_SIZES} />;
    $[0] = autoplay;
    $[1] = item;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const media = t1;
  const t2 = `relative ${aspectClass} overflow-hidden rounded-lg bg-neutral-100`;
  let t3;
  if ($[3] !== media || $[4] !== t2) {
    t3 = <div className={t2}>{media}</div>;
    $[3] = media;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const card = t3;
  if (item.href) {
    const t4 = item.alt || undefined;
    let t5;
    if ($[6] !== card || $[7] !== item.href || $[8] !== t4) {
      t5 = <IntentPrefetchLink href={item.href} className="block" aria-label={t4}>{card}</IntentPrefetchLink>;
      $[6] = card;
      $[7] = item.href;
      $[8] = t4;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  return card;
}
function VideoMedia(t0) {
  const $ = _c(7);
  const {
    item,
    autoplay
  } = t0;
  const videoRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== autoplay) {
    t1 = () => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      if (!autoplay) {
        return;
      }
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (prefersReducedMotion) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            video.play().catch(_temp2);
          } else {
            video.pause();
          }
        }
      }, {
        threshold: 0.5
      });
      observer.observe(video);
      return () => observer.disconnect();
    };
    t2 = [autoplay];
    $[0] = autoplay;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = !autoplay;
  let t4;
  if ($[3] !== item.posterUrl || $[4] !== item.url || $[5] !== t3) {
    t4 = <video ref={videoRef} src={item.url} poster={item.posterUrl} muted={true} loop={true} playsInline={true} preload="metadata" controls={t3} className="h-full w-full object-cover" />;
    $[3] = item.posterUrl;
    $[4] = item.url;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp2() {}